import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SearchBar from "src/components/common/SearchBar";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { ReportTabs } from "src/features/Reports/components/ReportTabs";
import {
  HELP_CENTER_CATEGORIES,
  MANUAL_SECTIONS,
  categoryLabel,
  getSectionsForCategory,
  sectionSearchScore,
  sectionSearchText,
  type HelpCenterCategoryId,
  type HelpCenterRouteKey,
  type ManualSection,
} from "src/features/RestaurantGuide/manualSections";
import { parseRestaurantSegment, restaurantSegment } from "src/utils/restaurantUrl";

function normalizeQuery(query: string): string {
  return query.trim().toLowerCase();
}

const GETTING_STARTED_FLOW = [
  "Set up store",
  "Add menu",
  "Add staff",
  "Printers",
  "POS app",
] as const;

function buildRouteMap(restaurantSlugAndId?: string): Record<HelpCenterRouteKey, { label: string; to: string }> {
  const { correlationId, slug } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  const segment = correlationId ? restaurantSegment(correlationId, slug) : "";
  const resolveRoute = (suffix: string) => (segment ? `/restaurants/${segment}/${suffix}` : "/my-restaurants");

  return {
    restaurantSetting: { label: "Open Settings", to: resolveRoute("restaurant-setting") },
    employee: { label: "Open Employees", to: resolveRoute("employee") },
    customers: { label: "Open Customers", to: resolveRoute("customers") },
    menu: { label: "Open Menu", to: resolveRoute("menu") },
    modifiers: { label: "Open Modifiers", to: resolveRoute("modifier") },
    combos: { label: "Open Combos", to: resolveRoute("combo") },
    orders: { label: "Open Orders", to: resolveRoute("orders") },
    financeTransactions: { label: "Open Payments", to: resolveRoute("finance/payments") },
    financeCashDrawer: { label: "Open Cash Drawer", to: resolveRoute("finance/cash-drawer") },
    financePayroll: { label: "Open Payroll", to: resolveRoute("finance/payroll") },
    promotions: { label: "Open Promotions", to: resolveRoute("promotions") },
    restaurantTable: { label: "Open Tables", to: resolveRoute("restaurant-table") },
    reportsOverview: { label: "Open Reports Overview", to: resolveRoute("reports/overview") },
    reportsSales: { label: "Open Sales Report", to: resolveRoute("reports/sales") },
    reportsPayments: { label: "Open Payments Report", to: resolveRoute("reports/payments") },
    reportsEmployees: { label: "Open Employees Report", to: resolveRoute("reports/employees") },
    reportsTax: { label: "Open Tax Report", to: resolveRoute("reports/tax") },
    events: { label: "Open Events", to: resolveRoute("events") },
  };
}

const linkPillClass =
  "inline-flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-medium text-[#111827] hover:bg-[#fafafa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] transition-colors";

function renderSectionLinks(
  section: ManualSection,
  routeMap: Record<HelpCenterRouteKey, { label: string; to: string }>
) {
  if (!section.relatedRouteKeys?.length) return null;

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {section.relatedRouteKeys.map((routeKey) => {
        const route = routeMap[routeKey];
        if (!route) return null;
        return (
          <Link key={`${section.id}-${routeKey}`} to={route.to} className={linkPillClass}>
            {route.label}
          </Link>
        );
      })}
    </div>
  );
}

function PlaybookCard({
  section,
  expanded,
  onToggle,
  routeMap,
  showCategoryBadge,
}: {
  section: ManualSection;
  expanded: boolean;
  onToggle: () => void;
  routeMap: Record<HelpCenterRouteKey, { label: string; to: string }>;
  showCategoryBadge: boolean;
}) {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full min-w-0 items-start gap-3 text-left px-4 py-4 sm:px-5 sm:py-4 hover:bg-[#fafafa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#FD7657]/40 transition-colors"
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1">
          {showCategoryBadge && (
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-[#6b7280]">{categoryLabel(section.category)}</p>
          )}
          <h2 className="text-base font-semibold text-[#111827] m-0">{section.title}</h2>
          <p className="mt-1 mb-0 text-sm leading-snug text-[#6b7280] line-clamp-2">{section.summary}</p>
        </div>
        <span className="shrink-0 text-lg leading-none text-[#9ca3af] w-6 text-center" aria-hidden>
          {expanded ? "−" : "+"}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-[#f1f1f1] px-4 py-4 sm:px-5 sm:pb-5 bg-[#fafafa]/60">
          {section.body?.trim() ? (
            <p className="m-0 mb-3 text-sm leading-relaxed text-[#374151] whitespace-pre-wrap">{section.body}</p>
          ) : null}
          {section.bullets.length > 0 ? (
            <ul className="m-0 space-y-2 pl-4 text-sm leading-snug text-[#374151] list-disc marker:text-[#9ca3af]">
              {section.bullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}
          {renderSectionLinks(section, routeMap)}
        </div>
      )}
    </div>
  );
}

export default function RestaurantGuidePageView() {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<HelpCenterCategoryId>("gettingStarted");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const routeMap = useMemo(() => buildRouteMap(restaurantSlugAndId), [restaurantSlugAndId]);
  const normalizedQuery = normalizeQuery(query);
  const searchActive = normalizedQuery.length > 0;

  const filteredSections = useMemo(() => {
    if (!normalizedQuery) return getSectionsForCategory(activeCategory);

    return [...MANUAL_SECTIONS]
      .map((section) => ({
        section,
        score: sectionSearchScore(section, normalizedQuery),
      }))
      .filter(({ section, score }) => score > 0 || sectionSearchText(section).includes(normalizedQuery))
      .sort((a, b) => b.score - a.score || a.section.title.localeCompare(b.section.title))
      .map(({ section }) => section);
  }, [activeCategory, normalizedQuery]);

  const activeCategoryMeta = HELP_CENTER_CATEGORIES.find((c) => c.id === activeCategory)!;
  const helpTabs = HELP_CENTER_CATEGORIES.map((c) => ({ id: c.id, label: c.tabLabel }));

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (normalizeQuery(value)) setExpandedId(null);
  };

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <ReportPageLayout
      title="Operations guide"
      compactToolbar
      toolbarMarginTop={8}
      toolbarMarginBottom={10}
      toolbar={
        <div className="flex w-full min-w-0 flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-4">
          <div className="min-w-0 flex-1 flex flex-col gap-2">
            {!searchActive && (
              <ReportTabs
                tabs={helpTabs}
                activeTabId={activeCategory}
                onSelect={(id) => {
                  setActiveCategory(id as HelpCenterCategoryId);
                  setExpandedId(null);
                }}
                inline
                inlineFullWidth
              />
            )}
            {searchActive && (
              <p className="text-sm text-[#6b7280] m-0">
                {filteredSections.length} match{filteredSections.length === 1 ? "" : "es"} across all sections. Clear
                search to browse by tab.
              </p>
            )}
          </div>
          <div className="flex w-full min-w-0 shrink-0 flex-col gap-1 md:w-[min(100%,20rem)] md:max-w-sm lg:items-end">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#6b7280] md:text-right">Search all topics</span>
            <SearchBar
              placeholder="Orders, payroll, printer, PIN…"
              value={query}
              onChange={handleQueryChange}
              className="w-full"
              ariaLabel="Search operations guide"
            />
          </div>
        </div>
      }
    >
      <div className="w-full max-w-[880px] min-w-0">
        {!searchActive && (
          <p className="text-sm text-[#6b7280] mb-4 mt-0">{activeCategoryMeta.description}</p>
        )}

        {!searchActive && activeCategory === "gettingStarted" && (
          <div
            className="mb-5 flex flex-wrap items-center gap-x-1 gap-y-1 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5 text-xs sm:text-sm text-[#4B5563]"
            aria-label="Recommended onboarding order"
          >
            <span className="font-semibold text-[#111827] mr-1">Recommended:</span>
            {GETTING_STARTED_FLOW.map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span className="text-[#D1D5DB] px-0.5">→</span>}
                <span className="whitespace-nowrap">
                  <span className="text-[#9CA3AF] font-medium">{i + 1}.</span> {label}
                </span>
              </React.Fragment>
            ))}
          </div>
        )}

        {filteredSections.length === 0 ? (
          <div className="rounded-xl border border-[#E5E7EB] bg-white px-4 py-8 text-center">
            <p className="m-0 text-base font-semibold text-[#111827]">No topics matched &quot;{query.trim()}&quot;</p>
            <p className="mt-2 mb-0 text-sm text-[#6b7280]">Try orders, payroll, receipt, PIN, or sync.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filteredSections.map((section) => (
              <PlaybookCard
                key={section.id}
                section={section}
                expanded={expandedId === section.id}
                onToggle={() => toggleExpand(section.id)}
                routeMap={routeMap}
                showCategoryBadge={searchActive}
              />
            ))}
          </div>
        )}
      </div>
    </ReportPageLayout>
  );
}
